import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";
import { toast } from "sonner";

const Login = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleError = params.get("google_error");
    if (googleError) {
      toast.error("Login Google belum berhasil. Coba lagi atau gunakan email dan password.");
      window.history.replaceState(null, "", "/login");
    }
  }, []);

  useEffect(() => {
    api.get("/auth/google/status")
      .then((res) => setGoogleReady(Boolean(res.data.configured)))
      .catch(() => setGoogleReady(false));
  }, []);

  const handleGoogleLogin = () => {
    window.location.href = `${API}/auth/google/start`;
  };

  const handleEmergentLogin = () => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", loginForm);
      setUser(res.data.user);
      toast.success(`Selamat datang, ${res.data.user.name}!`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/register", registerForm);
      toast.success("Berhasil! Cek email kamu untuk link verifikasi.");
      setRegisterForm({ email: "", password: "", name: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registrasi gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Logo size={56} className="mx-auto rounded-xl mb-4" />
          <h1 className="font-heading text-3xl font-black">Selamat datang di Kulintara</h1>
          <p className="text-sm text-muted-foreground mt-2">Masuk atau buat akun untuk mulai discover kuliner</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <Button onClick={handleGoogleLogin} disabled={!googleReady} variant="outline" className="h-11 rounded-full" data-testid="google-login-btn">
            <svg className="w-5 h-5 mr-2 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <span className="truncate">{googleReady ? "Google" : "Google disiapkan"}</span>
          </Button>
          <Button onClick={handleEmergentLogin} variant="outline" className="h-11 rounded-full" data-testid="emergent-login-btn">
            Emergent
          </Button>
        </div>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border"></div></div>
          <div className="relative flex justify-center text-xs"><span className="bg-background px-2 text-muted-foreground uppercase tracking-wider">atau</span></div>
        </div>

        <Tabs defaultValue="login">
          <TabsList className="grid grid-cols-2 mb-6 w-full">
            <TabsTrigger value="login" data-testid="tab-login">Masuk</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">Daftar</TabsTrigger>
          </TabsList>
          <TabsContent value="login">
            <form onSubmit={submitLogin} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input type="email" required value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })} data-testid="login-email" />
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" required value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} data-testid="login-password" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="login-submit">{loading ? "Memproses..." : "Masuk"}</Button>
              <p className="text-center text-xs">
                <Link to="/forgot-password" className="text-primary hover:underline" data-testid="forgot-link">Lupa password?</Link>
              </p>
            </form>
          </TabsContent>
          <TabsContent value="register">
            <form onSubmit={submitRegister} className="space-y-4">
              <div>
                <Label>Nama Lengkap</Label>
                <Input required value={registerForm.name} onChange={e => setRegisterForm({ ...registerForm, name: e.target.value })} data-testid="register-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" required value={registerForm.email} onChange={e => setRegisterForm({ ...registerForm, email: e.target.value })} data-testid="register-email" />
              </div>
              <div>
                <Label>Password (min 8 karakter)</Label>
                <Input type="password" required minLength={8} value={registerForm.password} onChange={e => setRegisterForm({ ...registerForm, password: e.target.value })} data-testid="register-password" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="register-submit">{loading ? "Memproses..." : "Daftar & Verifikasi Email"}</Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Login;
